import { useCallback, useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { canAccessAdmin } from "../utils/permissions";
import {
  ShieldCheck, Database, Export, Trash, Warning, FileText, SpinnerGap,
  CheckCircle, Eye, Users, Clock, MapPin, Gavel, Fingerprint, Cookie,
  Globe, Handshake, ListChecks, CaretDown, CaretUp, Scales
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";

import { API_BASE as API } from "../lib/apiBase";

function CollapsibleSection({ title, icon: Icon, children, defaultOpen = false, id }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-xl overflow-hidden" data-testid={`section-${id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 bg-card hover:bg-secondary/50 transition-colors text-left"
        data-testid={`toggle-${id}`}
      >
        <div className="flex items-center gap-2.5">
          {Icon && <Icon size={18} className="text-primary shrink-0" />}
          <h3 className="font-semibold text-sm">{title}</h3>
        </div>
        {open ? <CaretUp size={16} className="text-muted-foreground" /> : <CaretDown size={16} className="text-muted-foreground" />}
      </button>
      {open && <div className="px-4 pb-4 bg-card border-t border-border">{children}</div>}
    </div>
  );
}

export default function PrivacyPage() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const [policy, setPolicy] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // iter115 (Eric 2026-06-24) — Migration vers helper canonique canAccessAdmin.
  const isAdmin = canAccessAdmin(user);

  const loadData = useCallback(async () => {
    try {
      const [policyRes, inventoryRes] = await Promise.all([
        axios.get(`${API}/api/privacy/policy?lang=${lang}`, { withCredentials: true }),
        isAdmin ? axios.get(`${API}/api/privacy/inventory`, { withCredentials: true }).catch(() => null) : null,
      ]);
      setPolicy(policyRes.data);
      if (inventoryRes) setInventory(inventoryRes.data);
      if (isAdmin) {
        const [delRes, incRes] = await Promise.all([
          axios.get(`${API}/api/privacy/deletion-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/api/privacy/incidents`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setDeletionRequests(delRes.data || []);
        setIncidents(incRes.data || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [lang, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API}/api/privacy/export`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `knoria-données-personnelles-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    setExporting(false);
  };

  const handleDeletionRequest = async (scope) => {
    const ok = await confirmDialog(lang === "fr" ? "Confirmer la demande de suppression de vos données?" : "Confirm data deletion request?");
    if (!ok) return;
    try {
      await axios.post(`${API}/api/privacy/deletion-request`, { scope }, { withCredentials: true });
      toast.success(lang === "fr" ? "Demande soumise. Delai de traitement: 30 jours." : "Request submitted. Processing time: 30 days.");
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={32} className="animate-spin text-primary" /></div>;

  const tosSections = policy?.terms_of_service?.sections || [];
  const ppSections = policy?.privacy_policy?.sections || [];
  const dpaSections = policy?.dpa?.sections || [];

  const tabs = [
    { id: "overview", label: lang === "fr" ? "Conformité" : "Compliance", icon: ShieldCheck },
    { id: "tos", label: lang === "fr" ? "Conditions d'utilisation" : "Terms of Service", icon: Gavel, count: tosSections.length },
    { id: "privacy", label: lang === "fr" ? "Politique de confidentialité" : "Privacy Policy", icon: ShieldCheck, count: ppSections.length },
    { id: "dpa", label: "DPA", icon: Handshake, count: dpaSections.length },
    { id: "rights", label: lang === "fr" ? "Droits et données" : "Rights & Data", icon: Users },
  ];

  const sectionIcons = {
    pp_officer: ShieldCheck, pp_consent: ListChecks, pp_data_collected: Database,
    pp_ai_processing: Eye, pp_biometric: Fingerprint, pp_cookies: Cookie,
    pp_cross_border: Globe, pp_retention: Clock, pp_security: ShieldCheck,
    pp_incidents: Warning,
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="privacy-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/20 flex items-center justify-center">
          <Scales size={24} className="text-emerald-500" />
        </div>
        <div>
          <h1 className="text-xl font-bold font-['Outfit']" data-testid="privacy-title">
            {lang === "fr" ? "Loi 25 — Cadre juridique KNORIA" : "Law 25 — KNORIA Legal Framework"}
          </h1>
          <p className="text-xs text-muted-foreground">
            {lang === "fr"
              ? `Version ${policy?.version || "3.0"} — Conforme Loi 25, LPRPSP et PIPEDA`
              : `Version ${policy?.version || "3.0"} — Compliant with Law 25, LPRPSP and PIPEDA`}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="privacy-tabs">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                active
                  ? "bg-[#002FA7] text-white shadow-md"
                  : "bg-card border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon size={14} />
              {tab.label}
              {tab.count && <span className={`text-[10px] px-1 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-secondary"}`}>{tab.count}</span>}
            </button>
          );
        })}
      </div>

      {/* ══════════ TAB: CONFORMITE ══════════ */}
      {activeTab === "overview" && (
        <div className="space-y-4">
          {/* Compliance Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={18} className="text-emerald-600" weight="fill" />
                <span className="font-semibold text-sm">{lang === "fr" ? "Responsable designe" : "Officer designated"}</span>
              </div>
              <p className="text-xs text-muted-foreground">{policy?.privacy_officer?.name} — {policy?.privacy_officer?.email}</p>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={18} className="text-emerald-600" weight="fill" />
                <span className="font-semibold text-sm">{lang === "fr" ? "Chiffrement actif" : "Encryption active"}</span>
              </div>
              <p className="text-xs text-muted-foreground">AES-256 ({lang === "fr" ? "repos" : "rest"}) + TLS 1.3 ({lang === "fr" ? "transit" : "transit"})</p>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={18} className="text-emerald-600" weight="fill" />
                <span className="font-semibold text-sm">{lang === "fr" ? "Isolation multi-tenant" : "Multi-tenant isolation"}</span>
              </div>
              <p className="text-xs text-muted-foreground">{lang === "fr" ? "Données isolées par organisation" : "Data isolated per organization"}</p>
            </div>
          </div>

          {/* Lois applicables */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <Gavel size={16} className="text-primary" />
              {lang === "fr" ? "Lois applicables" : "Applicable laws"}
            </h3>
            <div className="space-y-2">
              {policy?.applicable_laws?.map((law, i) => (
                <div key={i} className="flex items-center gap-2 p-2.5 bg-secondary/50 rounded-lg">
                  <CheckCircle size={14} className="text-emerald-500 shrink-0" weight="fill" />
                  <span className="text-sm">{law}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Data Inventory (Admin) */}
          {inventory && (
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Database size={18} className="text-primary" />
                <h3 className="font-semibold text-sm">{lang === "fr" ? "Registre des données" : "Data inventory"}</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {inventory.data_categories?.map((cat, i) => (
                  <div key={i} className="bg-secondary/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">{cat.category}</p>
                    <p className="text-2xl font-bold font-['Outfit']">{cat.count}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {cat.contains_pi && <span className="text-[9px] px-1.5 py-0.5 bg-amber-500/10 text-amber-600 rounded font-medium">RP</span>}
                      <span className="text-[9px] text-muted-foreground flex items-center gap-0.5"><Clock size={8} />{cat.retention}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Third-party processors */}
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={18} className="text-primary" />
              <h3 className="font-semibold text-sm">{lang === "fr" ? "Sous-traitants et localisation" : "Processors & data locations"}</h3>
            </div>
            <div className="space-y-2">
              {policy?.third_party_processors?.map((tp, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 bg-secondary/50 rounded-lg text-sm">
                  <div className="flex-1">
                    <span className="font-medium">{tp.name}</span>
                    <span className="text-muted-foreground ml-2">— {tp.purpose}</span>
                  </div>
                  <span className="text-xs bg-secondary px-2 py-0.5 rounded">{tp.location}</span>
                  <span className="text-[10px] text-emerald-600">{tp.safeguards?.split(",")[0]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Deletion requests & incidents (Admin) */}
          {isAdmin && deletionRequests.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-5">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <Trash size={16} className="text-amber-600" />
                {lang === "fr" ? "Demandés de suppression en attente" : "Pending deletion requests"} ({deletionRequests.length})
              </h3>
              {deletionRequests.map(r => (
                <div key={r.id} className="flex items-center justify-between p-2 bg-card rounded-lg mb-1.5 text-sm">
                  <div><span className="font-medium">{r.user_email}</span><span className="text-muted-foreground ml-2">— {r.scope}</span></div>
                  <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("fr-CA")}</span>
                </div>
              ))}
            </div>
          )}
          {isAdmin && incidents.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-5">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <Warning size={16} className="text-red-600" />
                {lang === "fr" ? "Incidents de confidentialité" : "Privacy incidents"} ({incidents.length})
              </h3>
            </div>
          )}
        </div>
      )}

      {/* ══════════ TAB: CONDITIONS D'UTILISATION ══════════ */}
      {activeTab === "tos" && (
        <div className="space-y-2.5" data-testid="tos-content">
          {tosSections.map((section, i) => (
            <CollapsibleSection
              key={section.id}
              id={section.id}
              title={section.title}
              icon={FileText}
              defaultOpen={i < 2}
            >
              {section.definitions && (
                <div className="border border-border rounded-lg overflow-hidden mb-3 mt-2">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/50">
                      <tr>
                        <th className="p-2.5 text-left font-semibold w-1/4">{lang === "fr" ? "Terme" : "Term"}</th>
                        <th className="p-2.5 text-left font-semibold">{lang === "fr" ? "Définition" : "Definition"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.definitions.map((def, j) => (
                        <tr key={j} className="border-t border-border">
                          <td className="p-2.5 font-medium text-primary align-top">{def.term}</td>
                          <td className="p-2.5 text-muted-foreground">{def.def}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {section.list && (
                <ul className="space-y-1.5 mt-2 mb-3">
                  {section.list.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="text-red-500 font-bold mt-0.5 shrink-0">x</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
              {section.content && (
                <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line mt-2">
                  {section.content}
                </div>
              )}
            </CollapsibleSection>
          ))}
        </div>
      )}

      {/* ══════════ TAB: POLITIQUE DE CONFIDENTIALITE ══════════ */}
      {activeTab === "privacy" && (
        <div className="space-y-2.5" data-testid="privacy-policy-content">
          {ppSections.map((section, i) => {
            const Icon = sectionIcons[section.id] || FileText;
            return (
              <CollapsibleSection
                key={section.id}
                id={section.id}
                title={section.title}
                icon={Icon}
                defaultOpen={i < 2}
              >
                <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line mt-2">
                  {section.content}
                </div>
              </CollapsibleSection>
            );
          })}

          {/* Data collected table */}
          <div className="bg-card border border-border rounded-xl p-5 mt-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <Database size={16} className="text-primary" />
              {lang === "fr" ? "Tableau des renseignements personnels collectes" : "Personal information collected"}
            </h3>
            <div className="border border-border rounded-lg overflow-x-auto">
              <table className="w-full text-xs" data-testid="data-table">
                <thead className="bg-secondary/50">
                  <tr>
                    <th className="p-2.5 text-left font-semibold">Type</th>
                    <th className="p-2.5 text-left font-semibold">{lang === "fr" ? "Exemples" : "Examples"}</th>
                    <th className="p-2.5 text-left font-semibold">{lang === "fr" ? "Finalite" : "Purpose"}</th>
                    <th className="p-2.5 text-left font-semibold">{lang === "fr" ? "Conservation" : "Retention"}</th>
                    <th className="p-2.5 text-left font-semibold">{lang === "fr" ? "Base legale" : "Legal basis"}</th>
                  </tr>
                </thead>
                <tbody>
                  {policy?.data_collected?.map((d, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="p-2.5 font-medium">{d.type}</td>
                      <td className="p-2.5 text-muted-foreground">{d.examples}</td>
                      <td className="p-2.5">{d.purpose}</td>
                      <td className="p-2.5 text-muted-foreground">{d.retention}</td>
                      <td className="p-2.5">
                        <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded font-medium">{d.legal_basis}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ TAB: DPA ══════════ */}
      {activeTab === "dpa" && (
        <div className="space-y-2.5" data-testid="dpa-content">
          <div className="bg-card border border-border rounded-xl p-5 mb-3">
            <div className="flex items-center gap-3 mb-2">
              <Handshake size={20} className="text-primary" />
              <h3 className="font-semibold">{policy?.dpa?.title || "Entente de traitement des données (DPA)"}</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              {lang === "fr"
                ? "Entente regissant le traitement des renseignements personnels par KNORIA Inc. en tant que sous-traitant."
                : "Agreement governing the processing of personal information by KNORIA Inc. as a processor."}
            </p>
          </div>
          {dpaSections.map((section, i) => (
            <CollapsibleSection
              key={section.id}
              id={section.id}
              title={section.title}
              icon={FileText}
              defaultOpen={i < 2}
            >
              <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line mt-2">
                {section.content}
              </div>
            </CollapsibleSection>
          ))}
        </div>
      )}

      {/* ══════════ TAB: DROITS ET DONNEES ══════════ */}
      {activeTab === "rights" && (
        <div className="space-y-4" data-testid="rights-content">
          {/* User rights from policy */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <Users size={16} className="text-primary" />
              {lang === "fr" ? "Vos droits — Loi 25 / LPRPSP" : "Your rights — Law 25 / LPRPSP"}
            </h3>
            <div className="space-y-2" data-testid="rights-list">
              {policy?.user_rights?.map((r, i) => (
                <div key={i} className="border border-border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-sm">{lang === "fr" ? `Droit a l'${r.right.toLowerCase()}` : `Right to ${r.right.toLowerCase()}`}</h4>
                    {r.law_ref && <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded font-mono">{r.law_ref}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground">{r.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <ListChecks size={16} className="text-primary" />
              {lang === "fr" ? "Exercer vos droits" : "Exercise your rights"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Button variant="outline" onClick={handleExport} disabled={exporting} className="justify-start gap-2 h-auto py-3" data-testid="export-data-btn">
                <Export size={18} className="text-primary" />
                <div className="text-left">
                  <p className="font-medium text-sm">{lang === "fr" ? "Exporter mes données" : "Export my data"}</p>
                  <p className="text-[10px] text-muted-foreground">{lang === "fr" ? "Droit a la portabilite (JSON)" : "Portability right (JSON)"}</p>
                </div>
              </Button>
              <Button variant="outline" onClick={() => handleDeletionRequest("voice_data")} className="justify-start gap-2 h-auto py-3" data-testid="delete-voice-btn">
                <Trash size={18} className="text-amber-500" />
                <div className="text-left">
                  <p className="font-medium text-sm">{lang === "fr" ? "Supprimer mes données vocales" : "Delete my voice data"}</p>
                  <p className="text-[10px] text-muted-foreground">{lang === "fr" ? "Empreintes vocales uniquement" : "Voice fingerprints only"}</p>
                </div>
              </Button>
              <Button variant="outline" onClick={() => handleDeletionRequest("all")} className="justify-start gap-2 h-auto py-3 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20" data-testid="delete-all-btn">
                <Warning size={18} className="text-red-500" />
                <div className="text-left">
                  <p className="font-medium text-sm text-red-600">{lang === "fr" ? "Supprimer toutes mes données" : "Delete all my data"}</p>
                  <p className="text-[10px] text-muted-foreground">{lang === "fr" ? "Action irreversible — Delai 30 jours" : "Irreversible — 30 day delay"}</p>
                </div>
              </Button>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <ShieldCheck size={16} className="text-emerald-500" />
              {lang === "fr" ? "Contact — Responsable de la protection" : "Contact — Privacy officer"}
            </h3>
            <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
              {policy?.contact}
            </div>
          </div>

          {/* Incident procedure */}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-5">
            <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
              <Warning size={16} className="text-amber-600" />
              {lang === "fr" ? "Procédure en cas d'incident de confidentialité" : "Privacy incident procedure"}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{policy?.incident_procedure}</p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-xs text-muted-foreground text-center py-2 border-t border-border">
        KNORIA Inc. — {policy?.jurisdiction} — Version {policy?.version} | {policy?.effective_date}
      </div>
    </div>
  );
}
